import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import {ModalComponent} from '../../../../../../components/Layout/Modal'
import {useForm, FormProvider} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {
  SelectWrapper,
  InputTextWrapper,
  InputDateWrapper,
} from '../../../../../../components/UI/Form/Input'
import {ShowSnackBar} from '../../../../../../util/functions'
import {BedPseudoDto, CancelChangeColumn} from '../../../../models/cancel-change'
import {useCentralServices} from '../../../../hooks/useCentralServices'
import {SelectOption} from '../../../../../../models/utility.model'
import {formatDateDDMMYYYY, formatDateType2, formatDateType4} from '../../../../../../util/dates'
import {useModules} from '../../../../hooks/useModules'
import {useRooms} from '../../../../hooks/useRooms'
import {useBed} from '../../../../hooks/useBed'
import {
  createCancelChangeService,
  getBedPseudoService,
  getListCampsGuestService,
} from '../../../../services/cancel-change.service'
import Spinner from 'react-bootstrap/Spinner'
import {cancellationsChangesSchema} from '../../../../../../util/validationHoteleria'
import Swal from 'sweetalert2'
interface Props {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  getData: (formData: any) => void
  booking?: CancelChangeColumn
}

const ModalCancellationsChanges: FC<Props> = ({show, setShow, getData, booking}) => {
  const [reasons, setReasons] = useState<SelectOption[]>([])
  const [camps, setCamps] = useState<SelectOption[]>([])
  const [modules, setModules] = useState<SelectOption[]>([])
  const [rooms, setRooms] = useState<SelectOption[]>([])
  const [beds, setBeds] = useState<SelectOption[]>([])
  const [loadingBedData, setLoadingBedData] = useState<boolean>(false)
  const [enabledCheckin, setEnabledCheckin] = useState<boolean>(false)

  const methods = useForm<any>({
    resolver: yupResolver(cancellationsChangesSchema),
    defaultValues: {
      NroReserva_l: booking?.NroReserva,
      EliminarEstadias: true,
      FechaCheckout: formatDateType4(booking?.FechaCheckIn),
      FechaCheckout_l: formatDateType4(booking?.FechaCheckOut),
      Modulo_l: booking?.Modulo,
      Habitacion_l: booking?.Habitacion,
      Cama_l: booking?.Cama,
      CrearReserva: false,
      FechaInicio: formatDateType2(booking?.FechaInicio),
      FechaFin: formatDateType2(booking?.FechaFin),
      ModuloId: '',
      HabitacionId: '',
      CamaId: '',
      CrearCheckIn: false,
      FechaCheckIn: formatDateType2(booking?.FechaInicio),
      ImprimirCheckIn: false,
      ReservaCamaId: booking?.ReservaCamaId,
      MotivoAnulacionId: '',
      CampamentoId: booking?.CampamentoId,
    },
  })
  const {
    setValue,
    setError,
    watch,
    register,
    handleSubmit,
    formState: {errors},
  } = methods

  const {getMasterTable} = useCentralServices()
  const {getModulesAvailable} = useModules()
  const {getRoomsAvailable} = useRooms()
  const {getBedsAvailable} = useBed()

  useEffect(() => {
    getModulesFn(watch('CampamentoId'))
    getReasonsFn()
    getCampsGuestFn()
  }, [])

  const handleClose = () => {
    setShow(false)
  }

  const getReasonsFn = async () => {
    const data = await getMasterTable('MotivoAnulacionReserva', true)
    setReasons(data)
  }

  const getBedPseudoFn = async () => {
    try {
      const dto: BedPseudoDto = {
        CampamentoId: watch('CampamentoId'),
        FechaFin: watch('FechaFin'),
        FechaInicio: watch('FechaInicio'),
        HuespedId: booking?.HuespedId,
      }
      setLoadingBedData(true)
      const {data} = await getBedPseudoService(dto)
      if (!data.error) {
        await setValue('ModuloId', data.ModuloId)
        setError('ModuloId', {})
        await getRoomsFn(data.ModuloId)
        await setValue('HabitacionId', data.HabitacionId)
        setError('HabitacionId', {})
        await getBedsFn(data.HabitacionId)
        await setValue('CamaId', data.CamaId)
        setError('CamaId', {})
      } else {
        await setValue('ModuloId', '')
        await setValue('HabitacionId', '')
        await setValue('CamaId', '')
        ShowSnackBar('Hubo un error al asignar cama', 'error')
      }
      setLoadingBedData(false)
    } catch (error) {
      await setValue('ModuloId', '')
      await setValue('HabitacionId', '')
      await setValue('CamaId', '')
      setLoadingBedData(false)
    }
  }

  const getCampsGuestFn = async () => {
    const response = await getListCampsGuestService(booking?.HuespedId)
    const data = response.data.data || []
    const mappedCamps =
      data.map((item) => ({value: item.CampamentoId, label: item.CampamentoNombre})) || []
    setCamps(mappedCamps)
  }

  const getModulesFn = async (campId: string) => {
    const data = await getModulesAvailable({CampamentoId: campId})
    setModules(data)
  }

  const getRoomsFn = async (moduloId: string) => {
    const data = await getRoomsAvailable({CampamentoId: watch('CampamentoId'), ModuloId: moduloId})
    setRooms(data)
  }

  const getBedsFn = async (roomId: string) => {
    const data = await getBedsAvailable({CampamentoId: watch('CampamentoId'), HabitacionId: roomId})
    setBeds(data)
  }

  useEffect(() => {
    if (watch('EliminarEstadias')) {
      setValue('FechaCheckout', formatDateType4(booking?.FechaCheckIn))
      setError('FechaCheckout', {})
    }
  }, [watch('EliminarEstadias')])

  useEffect(() => {
    if (watch('FechaFin') && watch('FechaInicio') && watch('CrearReserva')) {
      const fechaInicio = new Date(watch('FechaInicio'))
      const fechaFin = new Date(watch('FechaFin'))
      if (fechaInicio <= fechaFin && watch('FechaFin')) {
        getBedPseudoFn()
      }
    }
  }, [watch('FechaFin'), watch('FechaInicio') && watch('CrearReserva')])

  useEffect(() => {
    if (watch('FechaInicio')) {
      const fechaInicio = new Date(watch('FechaInicio'))
      const fechaFin = new Date(watch('FechaFin'))
      const fechaCheckin = watch('FechaCheckIn') ? new Date(watch('FechaCheckIn')) : null
      if (fechaInicio > fechaFin && watch('FechaFin')) {
        setValue('FechaFin', '')
      }
      if (fechaCheckin && fechaInicio > fechaCheckin) {
        setValue('FechaCheckIn', watch('FechaInicio'))
      }
    }
  }, [watch('FechaInicio')])

  useEffect(() => {
    if (!watch('EliminarEstadias')) {
      setValue('FechaInicio', watch('FechaCheckout'))
    }
  }, [watch('FechaCheckout')])

  useEffect(() => {
    if (!watch('CrearCheckIn')) {
      setValue('FechaCheckIn', '')
      setError('FechaCheckIn', {})
    } else {
      if (!watch('CrearReserva')) {
        setValue('FechaCheckIn', formatDateType2(booking?.FechaInicio))
      } else {
        setValue('FechaCheckIn', watch('FechaInicio'))
      }
    }
  }, [watch('CrearCheckIn')])

  useEffect(() => {
    if (watch('CrearReserva')) {
      setValue('FechaCheckIn', watch('FechaInicio'))
      setEnabledCheckin(false)
    } else {
      setEnabledCheckin(true)
      setValue('CrearCheckIn', false)
      setValue('FechaCheckIn', formatDateType2(booking?.FechaInicio))
    }
  }, [watch('CrearReserva')])

  const handleSelectReason = (val: any) => {
    let value = val ? val.value : ''
    setValue('MotivoAnulacionId', value)
    setError('MotivoAnulacionId', {})
  }

  const handleCampChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('CampamentoId', value)
    setError('CampamentoId', {})
    setValue('ModuloId', '')
    setValue('HabitacionId', '')
    setValue('CamaId', '')
    getModulesFn(value)
    getBedPseudoFn()
    setRooms([])
    setBeds([])
  }

  const handleModuleChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('ModuloId', value)
    getRoomsFn(value)
    setError('ModuloId', {})
    setValue('HabitacionId', '')
    setValue('CamaId', '')
    setRooms([])
    setBeds([])
  }

  const handleRoomChange = (val: any) => {
    let value = val ? val.value : ''
    getBedsFn(value)
    setValue('HabitacionId', value)
    setError('HabitacionId', {})
    setValue('CamaId', '')
    setBeds([])
  }

  const onSubmit = async (data: any) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'De realizar cambios en la reserva',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si, guardar cambios',
      confirmButtonColor: 'var(--kt-primary)',
      cancelButtonText: 'No',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const dto = {
            ReservaCamaId: data.ReservaCamaId,
            MotivoAnulacionId: data.MotivoAnulacionId,
            FechaInicio: data.CrearReserva ? formatDateDDMMYYYY(data.FechaInicio) : '',
            FechaFin: data.CrearReserva ? formatDateDDMMYYYY(data.FechaFin) : '',
            CamaId: data.CamaId,
            FechaCheckOut: formatDateDDMMYYYY(data.FechaCheckout),
            EliminarEstadias: data.EliminarEstadias,
            CrearReserva: data.CrearReserva,
            CrearCheckIn: data.CrearCheckIn,
            FechaCheckIn: data.CrearCheckIn ? formatDateDDMMYYYY(data.FechaCheckIn) : '',
          }
          const response = await createCancelChangeService(dto)
          if (!response.data.error) {
            return true
          } else {
            Swal.fire({icon: 'error', text: response.data.message})
            return false
          }
        } catch (error) {
          Swal.showValidationMessage(`Hubo un error`)
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.close()
        setShow(false)
        getData({pageNumber: 1})
        ShowSnackBar('Se realizaron los cambios con éxito', 'sucess')
      }
    })
  }

  return (
    <ModalComponent
      show={show}
      title={'Anulaciones y Cambios'}
      handleSubmit={handleSubmit(onSubmit)}
      handleClose={handleClose}
      disabledSubmit={loadingBedData}
      size='xl'
    >
      <FormProvider {...methods}>
        <form>
          <div className='sdx_form card p-0'>
            <div>
              <div className='row'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputTextWrapper
                    labelText='NroReserva'
                    name='NroReserva_l'
                    type='alphaNumeric'
                    disabled
                  />
                </div>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <SelectWrapper
                    labelText='Motivo'
                    name='Motivo'
                    data={reasons}
                    required
                    onChangeValue={handleSelectReason}
                  />
                  {errors.MotivoAnulacionId && (
                    <p className='error-message'>{errors.MotivoAnulacionId?.message?.toString()}</p>
                  )}
                </div>
              </div>
            </div>
            <hr className='divider' />
            <div>
              <div className='row mb-4'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <div className='d-flex justify-content-between align-items-center pe-6 font-weight-bold'>
                    <label className='' style={{fontWeight: 800}}>
                      Eliminar Estadía:
                    </label>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input h-20px w-30px'
                        type='checkbox'
                        {...register('EliminarEstadias')}
                        id='switchDeleteStay'
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='row mb-1'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputDateWrapper
                    labelText='Fecha Checkout'
                    name='FechaCheckout'
                    disabled={watch('EliminarEstadias')}
                    minValue={
                      !watch('EliminarEstadias')
                        ? formatDateType4(booking?.FechaCheckIn)?.toString()
                        : '1900-01-01'
                    }
                  />
                </div>
              </div>
              <div className='mb-3' style={{fontWeight: 800}}>
                Datos de Reserva
              </div>
              <div className='row'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputTextWrapper
                    labelText='Módulo'
                    name='Modulo_l'
                    type='alphaNumeric'
                    disabled
                  />
                </div>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputTextWrapper
                    labelText='Habitación'
                    name='Habitacion_l'
                    type='alphaNumeric'
                    disabled
                  />
                </div>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputTextWrapper labelText='Cama' name='Cama_l' type='alphaNumeric' disabled />
                </div>
                {/* <div className='col-sm-12 col-md-6 col-lg-3'>
                  <InputDateWrapper labelText='Fecha Checkout' name='FechaCheckout_l' disabled />
                </div> */}
              </div>
            </div>
            <hr className='divider' />
            <div>
              <div className='row mb-4'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <div className='d-flex justify-content-between align-items-center pe-6'>
                    <label className='' style={{fontWeight: 800}}>
                      Generar Reserva:
                    </label>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input h-20px w-30px'
                        type='checkbox'
                        {...register('CrearReserva')}
                        id='switchGenerateBooking'
                      />
                    </div>
                  </div>
                </div>
              </div>
              {watch('CrearReserva') ? (
                <div>
                  <div className='row mb-1'>
                    <div className='col-sm-12 col-md-6 col-lg-3'>
                      <InputDateWrapper
                        labelText='Fecha Inicio Programada'
                        name='FechaInicio'
                        minValue={watch('EliminarEstadias') ? '1900-01-01' : watch('FechaCheckout')}
                        // maxValue={watch('EliminarEstadias') ? '3999-01-01' : watch('FechaCheckout')}
                        required
                      />
                    </div>
                    <div className='col-sm-12 col-md-6 col-lg-3'>
                      <InputDateWrapper
                        labelText='Fecha Fin Programada'
                        name='FechaFin'
                        minValue='1900-01-01'
                        required
                      />
                    </div>
                  </div>
                  <div className='row'>
                    {loadingBedData ? (
                      <div
                        className='d-flex justify-content-center align-items-center'
                        style={{height: '100px'}}
                      >
                        <Spinner animation='border' />
                      </div>
                    ) : (
                      <>
                        <div className='col-sm-12 col-md-6 col-lg-3'>
                          <SelectWrapper
                            labelText='Campamento'
                            name='CampamentoId'
                            data={camps}
                            required
                            onChangeValue={handleCampChange}
                          />
                        </div>
                        <div className='col-sm-12 col-md-6 col-lg-3'>
                          <SelectWrapper
                            labelText='Módulo'
                            name='ModuloId'
                            data={modules}
                            required
                            onChangeValue={handleModuleChange}
                          />
                        </div>
                        <div className='col-sm-12 col-md-6 col-lg-3'>
                          <SelectWrapper
                            labelText='Habitación'
                            name='HabitacionId'
                            data={rooms}
                            required
                            onChangeValue={handleRoomChange}
                          />
                        </div>
                        <div className='col-sm-12 col-md-6 col-lg-3'>
                          <SelectWrapper labelText='Cama' name='CamaId' data={beds} required />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            <hr className='divider' />
            <div>
              <div className='row mb-4'>
                <div className='col-sm-12 col-md-6 col-lg-3'>
                  <div className='d-flex justify-content-between align-items-center pe-6'>
                    <label className='' style={{fontWeight: 800}}>
                      Generar Checkin:
                    </label>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input h-20px w-30px'
                        type='checkbox'
                        {...register('CrearCheckIn')}
                        id='switchGenerateCheckin'
                        disabled={enabledCheckin}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {watch('CrearCheckIn') ? (
                <div>
                  <div className='row mb-1'>
                    <div className='col-sm-12 col-md-6 col-lg-3'>
                      <InputDateWrapper
                        labelText='Fecha Checkin'
                        name='FechaCheckIn'
                        minValue={
                          watch('CrearReserva') ? watch('FechaInicio') : watch('FechaCheckIn')
                        }
                        required
                      />
                    </div>
                    {/* <div className='col-sm-12 col-md-6 col-lg-3'>
                      <label className=''>Imprimir Checkin:</label>
                      <div
                        className='form-check form-switch form-check-custom form-check-solid'
                        style={{
                          height: '100%',
                          position: 'relative',
                          top: '-10px',
                        }}
                      >
                        <input
                          className='form-check-input h-20px w-30px'
                          type='checkbox'
                          {...register('ImprimirCheckIn')}
                          id='switchPrintCheckin'
                        />
                      </div>
                    </div> */}
                  </div>
                </div>
              ) : null}
              {/* <pre>{JSON.stringify(booking, null, 2)}</pre>
              <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
            </div>
          </div>
        </form>
      </FormProvider>
    </ModalComponent>
  )
}

export {ModalCancellationsChanges}
